import FormInput from "../components/inputs/FormInput.tsx";
import { InputType } from "../types/input.ts";
import ButtonLink from "../components/buttons/ButtonLink.tsx";
import { ButtonSize, ButtonType, ButtonVariant } from "../types/button.ts";
import Button from "../components/buttons/Button.tsx";
import React, { FormEvent, useEffect, useState } from "react";
import ValidatorsHelper from "../helpers/validatorsHelper.ts";
import { useNotification } from "../providers/NotificationProvider.tsx";
import { NotificationStatus } from "../components/notification/Notification.tsx";
import { useNavigate, useParams } from "react-router-dom";
import axios, { AxiosError } from "axios";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import apiNet from "../newApi/apiNet.ts";
import { useAuth } from "../hooks/useAuth.tsx";
import { BadRequestMessageStatusTypes } from "../exceptions/badRequestException.ts";
import AuthLayout from "../components/layouts/AuthLayout.tsx";
import FormWrapper from "../components/forms/FormWrapper.tsx";
import UtilsHelper from "../helpers/utilsHelper.ts";

const ChangePasswordPage = () => {
    const _notificationService = useNotification();
    const _authService = useAuth();
    const _navigate = useNavigate();
    const { token } = useParams();
    const [verificationToken, setVerificationToken] = useState<string | undefined | null>(undefined);
    const [isFinishedSuccess, setIsFinishedSuccess] = useState<boolean | undefined>(undefined);
    const [input, setInput] = useState({
        password: "",
        confirmPassword: ""
    });
    const [inputValid, setInputValid] = useState({
        passwordIsValid: false,
        confirmPasswordIsValid: false,
    })
    const [showErrors, setShowErrors] = useState(false);
    const handleSubmitEvent = async (e: FormEvent) => {
        e.preventDefault();
        if (inputValid.passwordIsValid && inputValid.confirmPasswordIsValid && verificationToken) {
            try {
                await _authService.resetPassword(verificationToken, input.password, input.confirmPassword);
                setIsFinishedSuccess(true);
            } catch (e) {
                const error = e as { code: number, status: BadRequestMessageStatusTypes };
                if (error.code === 400) {
                    if (error.status === "INVALID_TOKEN") {
                        setVerificationToken(null);
                        setIsFinishedSuccess(undefined);
                    } else if (error.status === "PASSWORD_AND_CONFIRM_ARE_NOT_EQUAL") {
                        _notificationService.showNotification("Niepowdzenie", "Hasło i potwierdz hasło różnią się od siebie", NotificationStatus.failure);
                    }
                } else if (error.code === 500) {
                    _navigate("/");
                } else {
                    setIsFinishedSuccess(false);
                }
            }
        } else {
            setShowErrors(true);
        }
    }

    const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            password: e.target.value
        }));
        setInputValid({
            passwordIsValid: ValidatorsHelper.isPasswordValid(e.target.value),
            confirmPasswordIsValid: e.target.value === input.confirmPassword
        });
    }

    const handleConfirmPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            confirmPassword: e.target.value
        }));
        setInputValid({
            passwordIsValid: ValidatorsHelper.isPasswordValid(input.password),
            confirmPasswordIsValid: e.target.value === input.password
        });
    }

    useEffect(() => {
        UtilsHelper.changeWindowDocumentTitle("Zmiana hasła");
        (async () => {
            try {
                if (token) {
                    const result = await apiNet.verifyChangePasswordToken(token);
                    if (result.status === 204)
                        setVerificationToken(token);
                }
            } catch (e) {
                const error = e as Error | AxiosError;
                if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
                    setVerificationToken(null);
                }
            }
        })()
    }, [token]);

    return (
        <AuthLayout>
            {verificationToken && isFinishedSuccess === undefined &&
                <FormWrapper>
                    <h1 className={"text-3xl font-medium"}>Zmiana hasła</h1>
                    <p className={"mt-5 text-neutral-300"}>Wprowadź nowe hasło oraz potwierdź nowe hasło, aby zmienić hasło</p>
                    <form
                        action="/recovery-password"
                        className={"w-full space-y-4 mt-10 text-left"}
                        onSubmit={(e) => handleSubmitEvent(e)}
                    >
                        <FormInput
                            label={"Hasło"}
                            name={"password"}
                            onInputChange={handlePasswordInputChange}
                            type={InputType.PASSWORD}
                            value={input.password}
                            placeholder={"Wpisz nowe hasło"}
                            className={"bg-neutral-700 ring-offset-neutral-900 py-2"}
                        />
                        {showErrors && !inputValid.passwordIsValid &&
                            <p className={"text-red-500 mt-2 font-light"}>
                                <span className={"font-normal"}>Hasło musizawierać:</span> <br />
                                - minimum 8 znaków <br />
                                - znak specjalny <br />
                                - dużą literę <br />
                                - cyfrę</p>}
                        <FormInput
                            label={"Potwierdź hasło"}
                            name={"confirmPassword"}
                            onInputChange={handleConfirmPasswordInputChange}
                            type={InputType.PASSWORD}
                            value={input.confirmPassword}
                            placeholder={"Potwierdź nowe hasło"}
                            className={"bg-neutral-700 ring-offset-neutral-900 py-2"}
                        />
                        {showErrors && !inputValid.confirmPasswordIsValid &&
                            <p className={"text-red-500 mt-2"}>Hasło i powtórz hasło różnią się od siebie</p>}
                        <p className={"text-xs text-neutral-400"}>Ta strona korzysta z usługi reCAPTCHA. Usługę
                            reguluje <ButtonLink to={"https://policies.google.com/terms"}
                                variant={ButtonVariant.SECONDARY}
                                targetType={"_blank"}
                                className={"text-xs pl-0 pr-0 pt-0 pb-0"}>regulamin
                                usługi</ButtonLink> Google.</p>
                        <Button
                            type={ButtonType.SUBMIT}
                            size={ButtonSize.BASE}
                            variant={ButtonVariant.PRIMARY}
                            className={"mx-auto block w-full py-1.5 text-lg"}
                        >Wyslij</Button>
                    </form>
                </FormWrapper>
            }
            {verificationToken && isFinishedSuccess === true &&
                <FormWrapper>
                    <CheckCircleIcon className={"w-16 h-16 text-green-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Zmiana hasła powiodła się</h1>
                    <p className={"text-neutral-400"}>Zmiana hasła zakończyła się sukcesem. Teraz możesz przejść
                        na <ButtonLink to={"/"} targetType={"_self"} variant={ButtonVariant.SECONDARY}
                            className={"pl-0 pr-0 pt-0 pb-0"}>stronę główną
                        </ButtonLink> i zalogować się za pomocą nowego hasła.</p>
                </FormWrapper>
            }
            {verificationToken && isFinishedSuccess === false &&
                <FormWrapper>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Zmiana hasła nie powiodła się</h1>
                    <p className={"text-neutral-400"}>Link jest nieprawidłowy lub wygasł. Proszę przejść na
                        stronę <ButtonLink to={"/odzyskiwanie-hasla"}
                            variant={ButtonVariant.SECONDARY}
                            className={"text-base pl-0 pr-0 pt-0 pb-0 text-sky-500"}>odzyskiwania
                            hasła</ButtonLink> i
                        wysłać ponownie
                        żadanie.</p>
                </FormWrapper>
            }
            {verificationToken === null &&
                <FormWrapper>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Link nieprawidłowy</h1>
                    <p className={"text-neutral-400"}>Link jest nieprawidłowy lub wygasł. Proszę przejść na
                        stronę <ButtonLink to={"/odzyskiwanie-hasla"}
                            variant={ButtonVariant.SECONDARY}
                            className={"text-base pl-0 pr-0 pt-0 pb-0 text-sky-500"}>odzyskiwania
                            hasła</ButtonLink> i
                        wysłać ponownie
                        żadanie.</p>
                </FormWrapper>
            }
            {verificationToken === undefined &&
                <section className={"self-center my-5 max-w-xs"}>
                    <svg
                        className={"mx-auto mt-16"}
                        width="44"
                        height="44"
                        viewBox="0 0 44 44"
                        xmlns="http://www.w3.org/2000/svg"
                        stroke="#fff"
                    >
                        <g fill="none" fillRule="evenodd" strokeWidth="2">
                            <circle cx="22" cy="22" r="1">
                                <animate
                                    attributeName="r"
                                    begin="0s"
                                    dur="1.8s"
                                    values="1; 20"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.165, 0.84, 0.44, 1"
                                    repeatCount="indefinite" />
                                <animate
                                    attributeName="stroke-opacity"
                                    begin="0s"
                                    dur="1.8s"
                                    values="1; 0"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.3, 0.61, 0.355, 1"
                                    repeatCount="indefinite" />
                            </circle>
                            <circle cx="22" cy="22" r="1">
                                <animate
                                    attributeName="r"
                                    begin="-0.9s"
                                    dur="1.8s"
                                    values="1; 20"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.165, 0.84, 0.44, 1"
                                    repeatCount="indefinite" />
                                <animate
                                    attributeName="stroke-opacity"
                                    begin="-0.9s"
                                    dur="1.8s"
                                    values="1; 0"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.3, 0.61, 0.355, 1"
                                    repeatCount="indefinite" />
                            </circle>
                        </g>
                    </svg>
                </section>
            }
        </AuthLayout>
    );
};

export default ChangePasswordPage;